import { formatLengthWithBasis } from '~/utils';
import type { Quantity } from '~/graphql/graphql';
import type { BundleProductLengthLike } from '~/components/ui/bundleQuantity';

export type ProductLengthGridItem = {
  id: string;
  quantity: Quantity;
  effectivePiecesPerBundle: number;
  platformProductsLength: BundleProductLengthLike & {
    id: string;
    lengthDisplayValue: string;
    isNominal?: boolean | null;
    platformProduct: { code?: string | null; name: string };
  };
};

interface ProductNameGroup<T extends ProductLengthGridItem> {
  name: string;
  items: T[];
}

export function lengthLabel(item: ProductLengthGridItem): string {
  const { lengthDisplayValue, isNominal } = item.platformProductsLength;
  return lengthDisplayValue
    ? formatLengthWithBasis(lengthDisplayValue, isNominal)
    : '';
}

function lengthSortValue(item: ProductLengthGridItem): number {
  const parsed = Number.parseFloat(
    item.platformProductsLength.lengthDisplayValue
  );
  return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
}

export function groupItemsByProductName<T extends ProductLengthGridItem>(
  items: T[]
): ProductNameGroup<T>[] {
  const groupsByName = new Map<string, T[]>();

  for (const item of items) {
    const name = item.platformProductsLength.platformProduct.name;
    const existing = groupsByName.get(name);
    if (existing) {
      existing.push(item);
    } else {
      groupsByName.set(name, [item]);
    }
  }

  return [...groupsByName.entries()].map(([name, groupItems]) => ({
    name,
    items: [...groupItems].sort(
      (left, right) => lengthSortValue(left) - lengthSortValue(right)
    ),
  }));
}
